import "./InstructorNotesSidebarPanel.scss";

import { get } from "lodash-es";
import { FC, useEffect, useMemo, useState } from "react";

import { INSTRUCTOR_NOTES_BLOCK_PATH } from "../../../shared/worksheets/constants";

import { CloseOverlayButton } from "./CloseOverlayButton";
import { ContentSidebarHeader } from "./ContentSidebarHeader";
import { ContentSidebarMain } from "./ContentSidebarMain";
import { OverlayContainer } from "./OverlayContainer";

import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { InstructorNotesIcon } from "mds/icons";
import { DocViewContext, DocViewContextType } from "providers/DocViewProvider";
import { useWorksheet } from "sharedb/ShareDBDoc";
import { useAppSelector } from "store/index";
import { selectCurrentPage } from "store/selectors";
import { rollbarAndLogError } from "utils/logger";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";
import { ProseMirrorRichTextField } from "worksheets/components/Field/RichText/ProseMirrorRichTextField";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const NotesToggleButton = ({ toggleNotes }: { toggleNotes: () => void }) => (
  <SquareIconButton kind="ghost" size="s" onClick={toggleNotes}>
    <InstructorNotesIcon size="s" />
  </SquareIconButton>
);

/*
 * Component for Instructor notes annotation on a given page.  Page is a worksheet but
 * will have only one defined content block.
 */
export const InstructorNotesSidebarPanel: FC = () => {
  const [showInstructorNotes, setShowInstructorNotes] = useState(true);
  const page = useAppSelector(selectCurrentPage);

  const docId = page?.instructor_notes_worksheet_id;
  const { doc } = useWorksheet(docId);

  const docViewContext: DocViewContextType = useMemo(
    () => ({
      readOnly: false,
      isGrading: false,
      canAuthorPage: true,
      isAuthoringPage: false,
      canWriteDoc: true,
      canInteractAsStudent: false,
      showResponses: false,
      isProjecting: false,
      isFeaturing: false,
      isReleased: false,
      isStaticContent: false,
      selectedPageTabVariant: null,
      hideStaticBlocks: false,
      pageId: "",
      accessId: "",
      isDone: false,
    }),
    [],
  );

  useEffect(() => {
    if (doc && !get(doc.data, INSTRUCTOR_NOTES_BLOCK_PATH)) {
      rollbarAndLogError(
        "Instructor notes does not have content block",
        doc.id,
        INSTRUCTOR_NOTES_BLOCK_PATH,
      );
    }
  }, [doc]);

  const toggleNotes = () => {
    setShowInstructorNotes(!showInstructorNotes);
  };

  if (!page?.worksheet_id) {
    return <div className="w-full p-4">{t("instructor_notes_box.no_selection")}</div>;
  }

  if (!showInstructorNotes) {
    return (
      <div>
        <NotesToggleButton toggleNotes={toggleNotes} />
      </div>
    );
  }

  if (!doc) {
    return null;
  }

  if (get(doc.data, INSTRUCTOR_NOTES_BLOCK_PATH) === undefined) {
    return (
      <div>
        <h1 className="page-title">{t("error.error")}</h1>
        <p>{t("error.cannot_display_instructor_notes")}</p>
      </div>
    );
  }

  return (
    <OverlayContainer>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <InstructorNotesIcon className="text-blue" />
          {getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.InstructorNotes)}
        </div>

        <CloseOverlayButton />
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false} className="h-full">
        <Card className="body-s h-full w-full overflow-y-auto p-0">
          <ErrorBoundary>
            <DocViewContext.Provider value={docViewContext}>
              <div className="instructor-notes relative w-full">
                <ProseMirrorRichTextField
                  characterCounter="NONE"
                  doc={doc}
                  eventName="Instructor notes"
                  path={INSTRUCTOR_NOTES_BLOCK_PATH}
                  placeHolderText={t("worksheet.placeholder.instructor_notes")}
                  showPlaceholder
                />
              </div>
            </DocViewContext.Provider>
          </ErrorBoundary>
        </Card>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};
